/**!
 * Created by Leonardo Cheslacki on 28/01/2025.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import StageController from './StageController';

import {HelpersPin} from './../helpers';

import Big from 'big.js';

import config from '../../../../config';

const {default: {domain, routes: {web}, implantation, text, button}} = config;

import WithType from './../helpers/WithType';

let _this = {};

/**
 *
 */
class StageLocalization extends StageController {

    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main = {}) {
        super(props);
        this.props = props;
        this.main = main;

        this.state = {
            data: [] || {}
        };

        this.$symbol_rectangle = null;
        this.$symbol_text = null;
        this.$section = null;

        _this = this;
    };

    $functions = {

        /**
         *
         * @param event
         * @param occ
         * @param text
         * @param x
         * @param y
         */
        handleMarker: function (event, {name, text, x, y}) {

            const {occ_tooltip, content_text} = _this.get();

            _this.$symbol_rectangle && occ_tooltip.removeChild(_this.$symbol_rectangle);
            _this.$symbol_text && occ_tooltip.removeChild(_this.$symbol_text);

            occ_tooltip.x = (_this.toIntegerOrFloat(x) + 37.1);
            occ_tooltip.y = _this.toIntegerOrFloat(y);

            for (let property of content_text.children) {
                if (property.name === name) {
                    property.occ_text.color = '#93857B';
                } else {
                    property.occ_text.color = '#442F20';
                }
            }

            /**
             * Create Legend
             * @type {string}
             */
            let
                font_attribute = `normal 20px 'Montserrat'`,
                color = _this.tintToHex('0xFFFFFF'),
                color_alfa = `${_this.tintToHex('0x6A4B36')}e6`;

            _this.$symbol_text = (new createjs.Text(_this.toCapitalize(text).toUpperCase(), font_attribute, color));
            _this.$symbol_rectangle = (new createjs.Shape());
            let bounds_text = _this.$symbol_text.getBounds();

            let
                big_text_width = (new Big(bounds_text.width)),
                big_text_height = (new Big(bounds_text.height)),
                big_padding_horizontal = (new Big(40)),
                big_padding_vertical = (new Big(30));

            let
                width = _this.bigToNumber(big_text_width.plus(big_padding_horizontal)),
                height = _this.bigToNumber(big_text_height.plus(big_padding_vertical)),
                symbol_x = (new Big(width)).div(_this.$controller.big_2),
                symbol_y = (new Big(height)).div(_this.$controller.big_2).plus(big_text_height.div(_this.$controller.big_2) + 60).neg();

            _this.$symbol_text.textAlign = 'center';
            _this.$symbol_text.x = _this.bigToNumber(symbol_x);
            /**
             * WARNING: The font has an effect variable on both fills, affecting the vertical center.
             */
            _this.$symbol_text.y = _this.bigToNumber(symbol_y);

            _this.$symbol_rectangle.graphics.beginFill(color_alfa).drawRoundRectComplex(0, -(height), width, height, height / 2, 0, height / 2, 0);

            occ_tooltip.addChild(_this.$symbol_rectangle);
            occ_tooltip.addChild(_this.$symbol_text);
        },

        /**
         *
         * @param event
         * @param section
         * @param xml
         */
        handleSection: function (event, {section, xml}) {
            _this.getForm(`${_this.$base_uri || domain}/${web.uri_xml}/section/${xml}`, {}, {
                done: (response) => {
                    let
                        object = _this.firstArray(_this.xmlToJson(response)),
                        products = _this.getObject(object, 'products');

                    _this.$section = section;

                    _this.setState({..._this.state, data: products}, _this.startLocalization);
                },
                fail: (response) => {
                    console.log(response);
                },
                always: () => {
                    _this.switchPins();
                }
            });

        },

        /**
         *
         * @param event
         * @param direction
         * @param original_height
         * @param current_height
         */
        handleScroll: function (event, {direction, original_height, current_height}){
            const {content_text, occ_menu: {y}} = _this.get();

            let down = ((current_height - original_height) - y);

            let to_y = y;

            if(direction === 'down'){
                to_y = -Math.round(down);
            }

            const tween = createjs.Tween.get(content_text, {loop: false, override: true});

            tween.to({y: to_y}, down, createjs.Ease.cubicOut).call((object) => {
                object.paused = true;
            });
        }
    };

    clonePinText = (_this) => {
        return {
            object: null,
            helpersPin: HelpersPin(_this)({current_name: 'pins'}),

            /**
             *
             * @returns {null}
             */
            get pinText() {
                return this.object;
            },

            /**
             *
             * @param object
             */
            set pinText(object) {
                this.object = object;
            },

            clone() {
                const _this_frame = _this.get(), {content_pin_text} = _this_frame;
                this.pinText = content_pin_text.clone();
            },

            /**
             *
             * @param string
             * @param color
             * @param font
             * @param size
             * @param weight
             * @param capitalize
             */
            setText(string, color, font, size, weight, capitalize) {
                const {occ_text} = this.object;

                let y = _this.hasString(string, '%n') ? 0 : occ_text.y;

                this.helpersPin.setText(occ_text, string, color, font, size, weight, null, y, capitalize);
            },

            /**
             *
             * @param color
             * @param occ
             */
            setColor(color, occ) {
                const occ_color = _this.verifyField(this.object, occ);
                this.helpersPin.setColor(occ_color, color);
            },

            /**
             *
             * @param x
             * @param y
             */
            setCoordinate(x, y) {
                this.helpersPin.setCoordinate(this.object, x, y);
            }
        }
    };

    switchPins = () => {
        const
            {xml} = this.props,
            _this_current = this.get(),
            {occ_tooltip, occ_title} = _this_current;

        this.$symbol_rectangle && occ_tooltip.removeChild(this.$symbol_rectangle);
        this.$symbol_text && occ_tooltip.removeChild(this.$symbol_text);

        for (let property in xml) {
            let occ = this.verifyField(_this_current, `occ_${property}`);

            if (!this.isEmpty(this.$section)) {
                occ_title.gotoAndStop(this.$section);
                occ && (occ.visible = (property === this.$section));
            } else {
                occ_title.gotoAndStop('empty');
                occ.visible = true;
            }
        }


    };

    startLocalization = () => {
        const
            {data} = this.state,
            {content_text, occ_menu, occ_scroll} = this.get();

        let
            text_y = 36,
            x = -15,
            y = 15,
            count = 0;

        content_text.removeAllChildren();

        this.recursiveSyncMap(data, 0, data.length, (item, next, stop, index) => {
            const
                product = this.verifyField(item, 'product'),
                pin_text = this.clonePinText(this),
                _allow = this.verifyField(item, 'attributes.allow').value,
                text = this.verifyField(product, 'text.value');

            if (this.hasString(_allow, 'localization')) {
                /**
                 * Clone pin text
                 */
                pin_text.clone();

                /**
                 * Clone pin text
                 */
                pin_text.setText(text, '0x442F20', 'MontserratRegular', 14, 'normal', true);
                pin_text.setCoordinate(x, y);

                let _index = this.padWithLeadingZeros(index + 1, 2);

                let name = `${pin_text.pinText.name}${_index}`;

                pin_text.pinText.name = name;

                pin_text.pinText.occ_align.occ_icon.gotoAndStop(this.$section);

                this.setHandleMouseDown(pin_text.pinText, this.$functions.handleMarker, {
                    name: name,
                    text: text,
                    x: this.verifyField(product, 'x.value'),
                    y: this.verifyField(product, 'y.value')
                });

                /**
                 * Clone pin text
                 */
                y += text_y;
                count += 1;

                content_text.addChild(pin_text.pinText);
            }
            next(true);
        }, () => {
            let original_height = occ_menu.nominalBounds.height;

            occ_menu.visible = true;
            occ_menu.scaleY = this.calcSize(original_height, y, 1);

            if (y > original_height) {
                occ_scroll.visible = true;

                const {btn_up, btn_down} = occ_scroll;

                this.setHandleMouseDown(btn_up, this.$functions.handleScroll, {direction: 'up', original_height: original_height, current_height: y});
                this.setHandleMouseDown(btn_down, this.$functions.handleScroll, {direction: 'down', original_height: original_height, current_height: y});
            } else {
                occ_scroll.visible = false;
                content_text.y = occ_menu.y;
            }
        });
    };

    initLocalization = () => {
        const
            {xml} = this.props,
            _this_current = this.get();

        this.switchPins();

        for (let property in xml) {
            let occ = this.verifyField(_this_current, `btn_${property}`);
            this.setHandleMouseDown(occ, this.$functions.handleSection, {section: property, xml: xml[property]});
        }
    };
}

/**
 *
 * @type {{}}
 */
StageLocalization.propTypes = {
    current_name: PropTypes.string,
    xml: PropTypes.string
};

/**
 *
 * @type {{}}
 */
StageLocalization.defaultProps = {
    current_name: null,
    xml: null
};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(StageLocalization, {className: 'StageLocalization'});
